import { CourierType } from 'const/CourierType';
import { PartitionType } from 'const/PartitionType';
import {
  fetchPartitioningCourier,
  fetchPartitioningList,
  fetchPartitioningZoneCount,
} from 'fetch/fetchPartitioning';
import { fetchAllRatings, ZoneRatingJson } from 'fetch/fetchRatings';
import { useCallback, useEffect, useRef, useState } from 'react';

export const usePartitionsData = (activeDate: string, activeRegion: string) => {
  const [couriers, setCouriers] = useState<Array<CourierType>>();
  const [partitions, setPartitions] = useState<Array<PartitionType>>();
  const [zoneCount, setZoneCount] = useState<Record<string, number>>();
  const [zoneRatings, setZoneRatings] = useState<ZoneRatingJson[]>();

  const activeDateRef = useRef<string>();
  const activeRegionRef = useRef<string>();

  const loadZoneRatings = useCallback(
    async (courierList: Array<CourierType>) => {
      const newZoneRatings: ZoneRatingJson[] = await fetchAllRatings(
        courierList.map(courier => courier.id)
      );

      /* Only replace when the value is changing */
      if (JSON.stringify(newZoneRatings) !== JSON.stringify(zoneRatings)) {
        setZoneRatings(newZoneRatings);
      }
    },
    [zoneRatings]
  );

  const loadCouriers = useCallback(
    async (date: string, region: string) => {
      const newCouriers = await fetchPartitioningCourier(date, region);

      /* Only replace when the value is changing */
      if (JSON.stringify(newCouriers) !== JSON.stringify(couriers)) {
        setCouriers(newCouriers);

        /* Load the Zone Ratings after courier has been fetched */
        loadZoneRatings(newCouriers).catch();
      }
    },
    [couriers, loadZoneRatings]
  );

  const loadPartitionsData = useCallback(
    async (date: string, region: string) => {
      const newPartitions = await fetchPartitioningList(date, region);

      /* Only replace when the value is changing */
      if (JSON.stringify(newPartitions) !== JSON.stringify(partitions)) {
        setPartitions(newPartitions);
      }
    },
    [partitions]
  );

  const loadZoneCount = useCallback(
    async (date: string, region: string) => {
      const newZoneCount = await fetchPartitioningZoneCount(date, region);

      /* Only replace when the value is changing */
      if (JSON.stringify(newZoneCount) !== JSON.stringify(zoneCount)) {
        setZoneCount(newZoneCount);
      }
    },
    [zoneCount]
  );

  /* Fetch all data */
  useEffect(() => {
    /* Prevent calling API multiple times because the AppRouter itself have re-rendering 3 times */
    if (
      activeDateRef.current === activeDate &&
      activeRegionRef.current === activeRegion
    ) {
      return;
    }

    /* Fetch Data */
    loadCouriers(activeDate, activeRegion).catch();
    loadPartitionsData(activeDate, activeRegion).catch();
    loadZoneCount(activeDate, activeRegion).catch();

    activeDateRef.current = activeDate;
    activeRegionRef.current = activeRegion;
  }, [
    activeDate,
    activeRegion,
    loadZoneCount,
    loadCouriers,
    loadPartitionsData,
    loadZoneRatings,
  ]);

  const refresh = () => {
    loadCouriers(activeDate, activeRegion).catch();
    loadPartitionsData(activeDate, activeRegion).catch();
    loadZoneCount(activeDate, activeRegion).catch();
  };

  return {
    isFetching: !couriers || !partitions || !zoneCount || !zoneRatings,
    couriers,
    list: partitions,
    zoneCount,
    zoneRatings,
    refresh,
  };
};
